/**
 * MomentSlideshowPage — /m/:slug/live — public TV slideshow.
 *
 * Fetches the approved gallery every ~6s (polling) and rotates a full-
 * screen cover animation. Perfect for a venue TV, projector, kiosk.
 *
 * Native features leveraged:
 *   • CSS transforms + willChange for smooth transitions.
 *   • navigator.wakeLock keeps the screen on for kiosk mode.
 */
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Loader2, Camera, QrCode } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL ||
             (typeof window !== "undefined" ? window.location.origin : "");

const POLL_MS   = 6000;
const ROTATE_MS = 5000;

export default function MomentSlideshowPage() {
  const { slug } = useParams();
  const [feed, setFeed]   = useState({ items: [], title: "", accent_hex: "#f59e0b" });
  const [idx,  setIdx]    = useState(0);
  const [ready, setReady] = useState(false);
  const wakeRef = useRef(null);

  // Poll gallery
  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const r = await fetch(`${API}/api/moments/m/${slug}/slideshow`);
        const j = await r.json();
        if (!alive) return;
        setFeed(j);
        setReady(true);
      } catch (_e) { /* silent */ }
    };
    load();
    const t = setInterval(load, POLL_MS);
    return () => { alive = false; clearInterval(t); };
  }, [slug]);

  // Rotate slide
  useEffect(() => {
    if (!feed.items?.length) return;
    const t = setInterval(() => {
      setIdx((i) => (i + 1) % feed.items.length);
    }, ROTATE_MS);
    return () => clearInterval(t);
  }, [feed.items?.length]);

  // Wake lock — keep screen on
  useEffect(() => {
    let released = false;
    (async () => {
      try {
        if ("wakeLock" in navigator) {
          wakeRef.current = await navigator.wakeLock.request("screen");
        }
      } catch (_e) { /* not supported */ }
    })();
    return () => {
      released = true;
      wakeRef.current?.release?.().catch(() => {});
    };
  }, []);

  if (!ready) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black text-white">
        <Loader2 className="animate-spin" size={28}/>
      </div>
    );
  }

  const current = feed.items?.[idx];
  const accent  = feed.accent_hex || "#f59e0b";

  if (!current) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-black text-white"
           data-testid="moments-slideshow-empty">
        <QrCode size={44} className="mb-4 opacity-60"/>
        <h1 className="text-3xl font-black mb-2">{feed.title || "Waiting for moments…"}</h1>
        <p className="text-stone-400 text-sm">Scan the QR to upload the first photo.</p>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black text-white overflow-hidden"
         data-testid="moments-slideshow-root">
      {/* Cover media */}
      {current.kind === "video" ? (
        <video
          key={current.media_id}
          src={current.public_url}
          autoPlay muted playsInline loop
          className="absolute inset-0 w-full h-full object-cover animate-[fadein_600ms_ease]"
        />
      ) : (
        <img
          key={current.media_id}
          src={current.public_url}
          alt=""
          className="absolute inset-0 w-full h-full object-cover animate-[fadein_600ms_ease]"
        />
      )}

      {/* Overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-black/40"/>

      {/* Bottom info bar */}
      <div className="absolute bottom-0 left-0 right-0 p-8 sm:p-12 flex items-end justify-between gap-6">
        <div className="min-w-0">
          <div className="text-xs uppercase font-bold tracking-widest mb-2"
               style={{ color: accent }}>
            <Camera size={14} className="inline mr-1"/> Live moments
          </div>
          <h1 className="text-3xl sm:text-5xl font-black drop-shadow-lg truncate">
            {feed.title}
          </h1>
          {current.caption && (
            <p className="mt-2 text-lg text-white/90 drop-shadow line-clamp-2">
              &ldquo;{current.caption}&rdquo;
            </p>
          )}
          {current.uploader_name && (
            <p className="mt-1 text-sm text-white/70">— {current.uploader_name}</p>
          )}
        </div>

        {/* QR corner */}
        <div className="hidden sm:block bg-white rounded-2xl p-3">
          <img
            src={`${API}/api/moments/m/${slug}/qr.png?size=384`}
            alt="Scan to upload"
            className="w-24 h-24 sm:w-28 sm:h-28"
          />
          <p className="text-[10px] font-bold text-stone-900 text-center mt-1">
            Scan to add yours
          </p>
        </div>
      </div>

      {/* Slide counter */}
      <div className="absolute top-6 right-6 rounded-full bg-white/10 backdrop-blur px-3 py-1 text-xs font-bold text-white/80">
        {idx + 1} / {feed.items.length}
      </div>

      <style>{`
        @keyframes fadein {
          from { opacity: 0; transform: scale(1.02); }
          to   { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </div>
  );
}
